import React, { useEffect, useState } from 'react'
import { whiteLabelAPI } from '@hpanel/web-common/api/client'
import type { WhiteLabel } from '@hpanel/web-common/api/types'

/**
 * Tenant Branding (Plan 8 v2 — white-label self-serve).
 *
 * Edit your own brand row. Resellers manage their tenants' panel
 * appearance here; admins also see this page to manage the platform
 * brand.
 */
export const Branding: React.FC = () => {
  const [form, setForm] = useState<Partial<WhiteLabel>>({
    brand_name: '',
    logo_url: '',
    brand_color: '#1f6feb',
    custom_subdomain: '',
    email_from: '',
    support_url: '',
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const r = await whiteLabelAPI.getMine()
        if (r && 'brand_name' in r) {
          setForm({
            brand_name: r.brand_name ?? '',
            logo_url: r.logo_url ?? '',
            brand_color: r.brand_color ?? '#1f6feb',
            custom_subdomain: r.custom_subdomain ?? '',
            email_from: r.email_from ?? '',
            support_url: r.support_url ?? '',
          })
        }
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load branding')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setInfo(null)
    try {
      // Strip empty strings to null so the server-side validators
      // don't apply format checks to fields the user left blank.
      const body: Partial<WhiteLabel> = {}
      ;(Object.keys(form) as (keyof WhiteLabel)[]).forEach((k) => {
        const v = form[k]
        if (v !== '' && v !== null && v !== undefined) {
          ;(body as Record<string, unknown>)[k] = v
        }
      })
      await whiteLabelAPI.upsertMine(body)
      setInfo('Branding saved.')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const clear = async () => {
    if (
      !window.confirm('Clear your brand row entirely? End-users will see the platform default.')
    ) {
      return
    }
    try {
      await whiteLabelAPI.deleteMine()
      setForm({
        brand_name: '',
        logo_url: '',
        brand_color: '#1f6feb',
        custom_subdomain: '',
        email_from: '',
        support_url: '',
      })
      setInfo('Brand row cleared.')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Clear failed')
    }
  }

  return (
    <div data-testid="branding-page" className="space-y-6">
      <div>
        <h1 data-testid="page-title" className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          Branding
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          White-label appearance for your account. Resellers: this
          controls what your end-users see.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-300 text-red-800 dark:text-red-200 rounded p-3 text-sm">
          {error}
        </div>
      )}
      {info && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 text-green-800 dark:text-green-200 rounded p-3 text-sm">
          {info}
        </div>
      )}

      {loading ? (
        <p className="text-gray-600 dark:text-gray-400">Loading…</p>
      ) : (
        <form
          onSubmit={save}
          data-testid="branding-form"
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4"
        >
          <Field
            label="Brand name"
            value={form.brand_name ?? ''}
            onChange={(v) => setForm({ ...form, brand_name: v })}
            placeholder="Acme Hosting"
          />
          <Field
            label="Logo URL"
            value={form.logo_url ?? ''}
            onChange={(v) => setForm({ ...form, logo_url: v })}
            placeholder="https://acme.example/logo.svg"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label="Brand color (hex #RRGGBB)"
              value={form.brand_color ?? ''}
              onChange={(v) => setForm({ ...form, brand_color: v })}
              placeholder="#1f6feb"
            />
            <Field
              label="Custom subdomain (DNS-safe)"
              value={form.custom_subdomain ?? ''}
              onChange={(v) =>
                setForm({ ...form, custom_subdomain: v.toLowerCase() })
              }
              placeholder="acme"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label="Email From"
              value={form.email_from ?? ''}
              onChange={(v) => setForm({ ...form, email_from: v })}
              placeholder="support@acme.example"
            />
            <Field
              label="Support URL"
              value={form.support_url ?? ''}
              onChange={(v) => setForm({ ...form, support_url: v })}
              placeholder="https://support.acme.example"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              data-testid="branding-save-button"
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg"
            >
              {saving ? 'Saving…' : 'Save branding'}
            </button>
            <button
              type="button"
              onClick={clear}
              data-testid="branding-clear-button"
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
            >
              Clear all
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

interface FieldProps {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
}

const Field: React.FC<FieldProps> = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
      {label}
    </label>
    <input
      type="text"
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
    />
  </div>
)
